import { ref } from "vue";
import { useRouter } from "vue-router";
import apiClient from "../api/index";

const isLoggedIn = ref(false);
const userEmail = ref("");
const userId = ref("");
const isAdmin = ref(false);
const isCreator = ref(false);

type JwtPayload = {
  exp?: number;
};

const clearAuthStorage = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("userId");
  localStorage.removeItem("email");
  localStorage.removeItem("isAdmin");
  localStorage.removeItem("isCreator");
};

const persistAuthResponse = (data: {
  token: string;
  userId: string | number;
  email: string;
  isAdmin: boolean;
  isCreator: boolean;
}) => {
  localStorage.setItem("token", data.token);
  localStorage.setItem("userId", String(data.userId));
  localStorage.setItem("email", data.email);
  localStorage.setItem("isAdmin", data.isAdmin ? "true" : "false");
  localStorage.setItem("isCreator", data.isCreator ? "true" : "false");
};

const getLoginRedirectQuery = (fullPath: string) => ({
  redirect: fullPath.startsWith("/") ? fullPath : "/",
});

const parseJwtPayload = (token: string): JwtPayload | null => {
  try {
    const payload = token.split(".")[1];
    if (!payload) {
      return null;
    }

    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decodedPayload = atob(normalizedPayload);

    return JSON.parse(decodedPayload) as JwtPayload;
  } catch (error) {
    console.error("Failed to parse auth token:", error);
    return null;
  }
};

export const isTokenExpired = (token: string) => {
  const payload = parseJwtPayload(token);
  if (!payload?.exp) {
    return true;
  }

  return payload.exp * 1000 <= Date.now();
};

export const initializeAuthState = () => {
  const token = localStorage.getItem("token");

  if (token && isTokenExpired(token)) {
    clearAuthStorage();
  }

  isLoggedIn.value = !!localStorage.getItem("token");
  userEmail.value = localStorage.getItem("email") || "";
  userId.value = localStorage.getItem("userId") || "";
  isAdmin.value = localStorage.getItem("isAdmin") === "true";
  isCreator.value = localStorage.getItem("isCreator") === "true";
};

export function useAuth() {
  const router = useRouter();

  const checkLoginStatus = () => {
    initializeAuthState();
  };

  const verifyAuth = async () => {
    const token = localStorage.getItem("token");
    if (!token || isTokenExpired(token)) {
      clearAuthStorage();
      checkLoginStatus();
      router.push({
        name: "Login",
        query: getLoginRedirectQuery(router.currentRoute.value.fullPath),
      });
      return null;
    }

    try {
      const response = await apiClient.get("/me", {
        headers: { Authorization: `Bearer ${token}` },
      });

      localStorage.setItem("isAdmin", response.data.isAdmin ? "true" : "false");
      localStorage.setItem("isCreator", response.data.isCreator ? "true" : "false");
      localStorage.setItem("email", response.data.email);
      localStorage.setItem("userId", response.data.userId);

      checkLoginStatus();

      return response.data;
    } catch (error: any) {
      console.error("Auth check failed:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        clearAuthStorage();
        checkLoginStatus();
        router.push({
          name: "Login",
          query: getLoginRedirectQuery(router.currentRoute.value.fullPath),
        });
      }
      return null;
    }
  };

  const login = async (email: string, password: string) => {
    const response = await apiClient.post("/login", { email, password });

    persistAuthResponse(response.data);

    checkLoginStatus();

    return response.data;
  };

  const loginWithGoogle = async (credential: string) => {
    const response = await apiClient.post("/login/google", { credential });

    persistAuthResponse(response.data);

    checkLoginStatus();

    return response.data;
  };

  const logout = () => {
    clearAuthStorage();
    checkLoginStatus();
  };

  return {
    isLoggedIn,
    userEmail,
    userId,
    isAdmin,
    isCreator,
    checkLoginStatus,
    verifyAuth,
    login,
    loginWithGoogle,
    logout,
  };
}
